import {
	BackIcon,
	cn,
	IconButton,
	Input,
	XIcon,
} from "@au-schein-de/au-ui-library";
import { type ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useStuckObserver } from "~/features/products/pharmacy-picker/use-stuck-observer.hook";

type PharmacyPickerMobileSearchOverlayProps = {
	open: boolean;
	onClose: () => void;
	value: string;
	onValueChange: (value: string) => void;
	placeholder?: string;
	toolbar?: ReactNode;
	children: ReactNode;
};

/**
 * Full-screen mobile search for the pharmacy picker. Mirrors the existing
 * mobile full-screen pattern (portal to body + `fixed inset-0` + body scroll
 * lock, `md:hidden`), with a search header (back + input + clear) above the
 * results. Desktop never renders it; the dropdown is used there instead.
 */
export function PharmacyPickerMobileSearchOverlay({
	open,
	onClose,
	value,
	onValueChange,
	placeholder,
	toolbar,
	children,
}: PharmacyPickerMobileSearchOverlayProps) {
	const { t } = useTranslation("common");
	const inputRef = useRef<HTMLInputElement>(null);
	const { sentinelRef, isStuck } = useStuckObserver(open);

	useEffect(() => {
		if (!open) return;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		// Focus the field (and raise the keyboard) when the search opens.
		inputRef.current?.focus();
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [open]);

	if (!open || typeof document === "undefined") {
		return null;
	}

	return createPortal(
		<div
			className="fixed inset-0 z-[120] flex flex-col bg-gray-100 md:hidden"
			data-testid="pharmacy-picker-mobile-search"
		>
			<div
				className={cn(
					"relative z-10 shrink-0 bg-white p-3 transition-shadow",
					isStuck && "shadow-[0_4px_20px_0_rgba(33,42,54,0.1)]",
				)}
			>
				<Input
					ref={inputRef}
					name="search"
					data-testid="vendor-search-mobile"
					placeholder={placeholder}
					value={value}
					onChange={(event) => onValueChange(event.target.value)}
					leftIcon={
						<IconButton
							icon={<BackIcon size={6} colorToken="gray-700" />}
							onClick={onClose}
							shape="circle"
							size="sm"
							variant="input"
							aria-label={t("back")}
							data-testid="vendor-search-mobile-back"
						/>
					}
					rightIcon={
						value !== "" ? (
							<IconButton
								icon={<XIcon size={6} colorToken="gray-700" />}
								onClick={() => onValueChange("")}
								aria-label={t("header.addressModal.clearSearch")}
								shape="circle"
								size="sm"
								variant="input"
								data-testid="vendor-search-mobile-clear"
							/>
						) : undefined
					}
				/>
				{toolbar ? <div className="mt-3">{toolbar}</div> : null}
			</div>
			<div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray-100">
				<div
					ref={sentinelRef}
					aria-hidden="true"
					className="pointer-events-none h-px w-full shrink-0"
				/>
				{children}
			</div>
		</div>,
		document.body,
	);
}
